import getHttpsCallable from "../../utils/getHttpsCallable"

// Calls the admin-zendeskRefresh Cloud Function, which performs the
// `grant_type: refresh_token` exchange against Zendesk server-side (the
// /oauth/tokens endpoint can't be called directly from the browser).
const zendeskRefresh = getHttpsCallable("admin-zendeskRefresh")

const ACCESS_TOKEN_KEY = "zauth"
const REFRESH_TOKEN_KEY = "zrefresh"
const ACCESS_EXPIRES_AT_KEY = "zauth_expires_at"
const REFRESH_EXPIRES_AT_KEY = "zrefresh_expires_at"

// Refresh slightly before the real expiry to absorb clock skew / in-flight
// requests.
const EXPIRY_SKEW_MS = 60_000

export interface ZendeskTokenSet {
  accessToken: string
  refreshToken?: string | null
  expiresIn?: number | null
  refreshTokenExpiresIn?: number | null
}

export function setZendeskTokens (tokens: ZendeskTokenSet) {
  if (!tokens.accessToken) {
    // A failed exchange must not persist an empty/invalid token.
    console.error("Refusing to store an empty Zendesk access token.")
    return
  }

  const now = Date.now()

  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken)

  if (tokens.refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken)
  }

  if (tokens.expiresIn) {
    localStorage.setItem(
      ACCESS_EXPIRES_AT_KEY,
      String(now + tokens.expiresIn * 1000),
    )
  }
  else {
    localStorage.removeItem(ACCESS_EXPIRES_AT_KEY)
  }

  if (tokens.refreshTokenExpiresIn) {
    localStorage.setItem(
      REFRESH_EXPIRES_AT_KEY,
      String(now + tokens.refreshTokenExpiresIn * 1000),
    )
  }
  else {
    localStorage.removeItem(REFRESH_EXPIRES_AT_KEY)
  }
}

// Treat a missing token — or a literal "undefined"/"null" string persisted by a
// failed exchange — as "not signed in", so the panel shows the sign-in button
// instead of sending `Bearer undefined`.
function readToken (key: string) {
  const value = localStorage.getItem(key)
  if (!value || value === "undefined" || value === "null") {
    return null
  }
  return value
}

export function getAccessToken () {
  return readToken(ACCESS_TOKEN_KEY)
}

export function getRefreshToken () {
  return readToken(REFRESH_TOKEN_KEY)
}

export function clearZendeskTokens () {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(ACCESS_EXPIRES_AT_KEY)
  localStorage.removeItem(REFRESH_EXPIRES_AT_KEY)
}

export function isAccessTokenExpired () {
  const expiresAt = localStorage.getItem(ACCESS_EXPIRES_AT_KEY)
  // Tokens issued by the old (pre-refresh) flow have no recorded expiry — treat
  // them as valid so existing sessions keep working until they hit a 401.
  if (!expiresAt) {
    return false
  }
  return Date.now() >= Number(expiresAt) - EXPIRY_SKEW_MS
}

let refreshPromise: Promise<string | null> | null = null

// Exchanges the stored refresh token for a fresh access token. Concurrent
// callers share a single in-flight request. Returns the new access token, or
// null if there is no refresh token or the refresh failed (in which case the
// stored tokens are cleared so the caller can restart the sign-in flow).
export async function refreshZendeskToken (): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise
  }

  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    return null
  }

  refreshPromise = (async () => {
    try {
      const response = await zendeskRefresh({ refresh_token: refreshToken })
      const data = response.data
      setZendeskTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: data.expiresIn,
        refreshTokenExpiresIn: data.refreshTokenExpiresIn,
      })
      return data.accessToken as string
    }
    catch (err) {
      console.error("Zendesk token refresh failed.", err)
      // The refresh token is invalid/expired — force a fresh sign-in.
      clearZendeskTokens()
      return null
    }
    finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}
